import type { OperationsNode } from '../types/operations.types'

export interface FilteredTree {
  nodes: OperationsNode[]
  matchCount: number
  matchedIds: Set<string>
}

const normalize = (value: string): string => value.trim().toLowerCase()

/**
 * Filters the tree by a search term, returning a new tree — the input is never
 * mutated. A node survives if it matches, or if any descendant matches.
 *
 * A node that matches itself keeps ALL of its descendants, so selecting a
 * division from a search still shows its full roster.
 *
 * An empty or whitespace-only term returns the original array by reference, so
 * callers can rely on referential equality to detect "not searching".
 */
export const filterOperationsTree = (
  nodes: OperationsNode[],
  term: string
): FilteredTree => {
  const needle = normalize(term)
  if (!needle) {
    return { nodes, matchCount: 0, matchedIds: new Set() }
  }

  const matchedIds = new Set<string>()

  const walk = (list: OperationsNode[]): OperationsNode[] =>
    list.reduce<OperationsNode[]>((kept, node) => {
      const isMatch = normalize(node.name).includes(needle)
      if (isMatch) matchedIds.add(node.id)

      // A self-match keeps the whole subtree; otherwise keep only the branches
      // that lead to a match.
      const children = isMatch ? node.children : walk(node.children)

      if (isMatch || children.length > 0) {
        kept.push({ ...node, children } as OperationsNode)
      }
      return kept
    }, [])

  const filtered = walk(nodes)
  return { nodes: filtered, matchCount: matchedIds.size, matchedIds }
}
